import { ApiResponse } from '../types';

// Simple in-memory cache for API responses
class ApiCache {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();

  set(key: string, data: any, ttl: number = 300000) { // 5 minutes default TTL
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  get(key: string): any | null {
    const item = this.cache.get(key);
    if (!item) return null;

    // Check if expired
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  clear(pattern?: string) {
    if (pattern) {
      // Clear entries matching pattern
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
        }
      }
    } else {
      this.cache.clear();
    }
  }

  // Clear cache for specific endpoints
  clearEndpoint(endpoint: string) {
    this.clear(endpoint);
  }

  // Get cache statistics (for debugging)
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

// Debounce utility
function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

// Cache instance
const cache = new ApiCache();

// Debounced cache clearing
const debouncedClearCache = debounce((pattern: string) => {
  cache.clear(pattern);
}, 100);

export class OptimizedApiService {
  private static instance: OptimizedApiService;
  private baseApi: any;

  private constructor(baseApi: any) {
    this.baseApi = baseApi;
  }

  static getInstance(baseApi: any) {
    if (!OptimizedApiService.instance) {
      OptimizedApiService.instance = new OptimizedApiService(baseApi);
    }
    return OptimizedApiService.instance;
  }

  // Generic cached GET request
  async cachedGet<T>(
    url: string,
    params?: any,
    ttl: number = 300000, // 5 minutes
    useCache: boolean = true
  ): Promise<ApiResponse<T>> {
    const cacheKey = `${url}${params ? JSON.stringify(params) : ''}`;
    
    // Check cache first
    if (useCache) {
      const cached = cache.get(cacheKey);
      if (cached) {
        console.log(`🚀 Cache HIT for ${cacheKey}`);
        return cached;
      }
    }

    try {
      console.log(`📡 API CALL for ${cacheKey}`);
      const response = await this.baseApi.get(url, { params });
      
      // Cache successful responses
      if (response.status === 200 && useCache) {
        cache.set(cacheKey, response.data, ttl);
        console.log(`💾 Cache SET for ${cacheKey}`);
      }
      
      return response.data;
    } catch (error) {
      console.error(`❌ API ERROR for ${cacheKey}:`, error);
      throw error;
    }
  }

  // Generic POST/PUT/DELETE request with cache invalidation
  async mutateAndInvalidate<T>(
    method: 'post' | 'put' | 'delete',
    url: string,
    data?: any,
    invalidatePattern?: string
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.baseApi[method](url, data);
      
      // Invalidate cache for related endpoints
      if (invalidatePattern) {
        debouncedClearCache(invalidatePattern);
      }
      
      return response.data;
    } catch (error) {
      console.error(`❌ ${method.toUpperCase()} ERROR for ${url}:`, error);
      throw error;
    }
  }

  // Clear cache for specific patterns
  clearCache(pattern?: string) {
    if (pattern) {
      debouncedClearCache(pattern);
    } else {
      cache.clear();
    }
  }

  // Get cache stats (for debugging)
  getCacheStats() {
    return cache.getStats();
  }
}

// Export cache utilities for manual cache management
export { cache, debouncedClearCache };
